
import Vue, { PropType } from 'vue'
import * as d3 from 'd3'
// import * as d3Array from 'd3-array'
import { group } from 'd3-array'
import { ScaleSequential, HSLColor } from 'd3'

import { getYear, getMonth, getUtcDate } from '@/helpers/dateHelpers'

declare module 'd3-array' {
  /**
   * Groups the specified array of values into a Map from key to array of value.
   * @param a The array to group.
   * @param key The key function.
   * @param key2 The key function.
   */
  export function group<TObject, TKey, TKey2>(
    a: Iterable<TObject>,
    key: (value: TObject) => TKey,
    key2: (value: TObject) => TKey2
  ): Map<TKey, Map<TKey2, TObject[]>>
}

// const minString = (values: string[]): string => d3.min(values)

const TODAY = new Date().toISOString().split('T')[0]

const timeWeek = d3.utcMonday
const countDay = (d: Date) => (d.getUTCDay() + 6) % 7

const formatValue = d3.format(',')
const formatDate = d3.utcFormat('%x')
const formatDay = (d: Date) => 'SMTWTFS'[d.getUTCDay()]
// const formatMonth = d3.utcFormat("%b")

const ZERO_COLOR = 'rgba(239, 239, 239, 1)'

// export const interpolateHueLightness = (hue: number, saturation = 100, min = 50, max = 100) => (v: number) => `hsl(${Math.max(0, Math.min(360, hue))}, ${saturation}%, ${Math.min(100, max) - (v * (Math.min(100, max) - Math.max(0, min)))}%)`
export const interpolateHueLightness =
  (hue: number, saturation = 1, min = 0.5, max = 1) =>
  (v: number) =>
    d3.hsl(
      hue,
      saturation,
      d3.scaleLinear().domain([0, 1]).range([max, min])(v) ?? 0.5
    )

interface CalendarDataItem {
  date: string
  value: number
  type: string
}

// type CalendarYearGroupItem = [number, CalendarDateGroupItem[]]
type CalendarYearMonthGroupItem = [string, CalendarDateGroupItem[]]
type CalendarDateGroupItem = [string, CalendarDataItem[]]
// type CalendarGroupItem  = [number, [Date, CalendarDataItem[]]]

type DateValues = [string, CalendarDataItem[]]

export default Vue.extend({
  props: {
    data: Array as PropType<Array<CalendarDataItem>>,

    cellSize: {
      type: Number,
      default: 17,
    },
    // width: {
    //   type: Number,
    //   default: 954,
    // },
    maxWidth: {
      type: [Number, String],
      default: undefined,
    },
    monthGap: {
      type: Number,
      default: 3,
    },
  },
  data: () => ({
    svg: d3.select('*') as unknown as d3.Selection<
      Element,
      CalendarYearMonthGroupItem[],
      null,
      undefined
    >, // <HTMLElement, {}, CalendarYearGroupItem, any>,
  }),
  computed: {
    yearsMonths(): Array<CalendarYearMonthGroupItem> {
      return Array.from(
        group(
          this.data,
          (d) =>
            [
              getYear(d.date),
              getMonth(d.date).toString().padStart(2, '0'),
            ].join('-'),
          (d) => d.date
        )
      )
        .map(
          ([key, values]: [
            string,
            Map<string, CalendarDataItem[]>
          ]): CalendarYearMonthGroupItem => [key, Array.from(values)]
        )
        .sort((a, b) => (a[0] > b[0] ? -1 : 1))
    },
    startDate(): string {
      return d3.min(this.data, (d) => d.date) || TODAY
    },
    endDate(): string {
      return d3.max(this.data, (d) => d.date) || TODAY
    },
    height(): number {
      return (
        (timeWeek.count(getUtcDate(this.startDate), getUtcDate(this.endDate)) +
          1 +
          1) *
          this.cellSize +
        (this.yearsMonths.length - 1) * this.monthGap
      )
    },
    width(): number {
      return this.cellSize * 7 + 50
    },
    color(): ScaleSequential<string | HSLColor> {
      const max = d3.max(
        this.yearsMonths
          .map(([, values1]) =>
            values1.map(([, values2]) =>
              values2.reduce((tot, item) => tot + item.value, 0)
            )
          )
          .flat()
      )
      return d3
        .scaleSequential(interpolateHueLightness(0, 1, 0.5, 0.95))
        .domain([0, max || 0])
    },
  },
  watch: {
    data() {
      this.redraw()
    },
  },
  mounted() {
    this.draw()
  },
  methods: {
    pathMonth(t: Date) {
      const n = 7
      const d = Math.max(0, Math.min(n, countDay(t)))
      const w = timeWeek.count(d3.utcYear(t), t)
      return `${
        d === 0
          ? `M${w * this.cellSize},0`
          : d === n
          ? `M${(w + 1) * this.cellSize},0`
          : `M${(w + 1) * this.cellSize},0V${d * this.cellSize}H${
              w * this.cellSize
            }`
      }V${n * this.cellSize}`
    },
    redraw() {
      this.svg.select('g.main-group').selectAll('*').remove()

      this.drawData()
    },
    drawData() {
      const year = this.svg
        .select('g.main-group')
        .selectAll('.year-month')
        .data(this.yearsMonths)
        .join('g')
        // .attr('transform', (_, i) => `translate(40.5,${this.height * i + this.cellSize * 1})`)
        // .attr('transform', (_, i) => `translate(40.5, ${0})`)
        .attr(
          'transform',
          (_, i) => `translate(50, ${this.cellSize + 1 + i * this.monthGap})`
        )
        .attr('class', 'year-month')
      // .attr("transform", (d: CalendarYearGroupItem, i: number) =>
      //   `translate(40.5,${this.height * i + this.cellSize * 1.5})`);

      year
        .append('text')
        .attr('x', -10)
        // .attr("y", ([key], i) => (timeWeek.count(getUtcDate(key + '-01'), getUtcDate(this.endDate)) + 1) * this.cellSize - (this.cellSize / 2) - (i * this.monthGap))
        .attr(
          'y',
          ([key]) =>
            (timeWeek.count(getUtcDate(key + '-01'), getUtcDate(this.endDate)) +
              1) *
              this.cellSize -
            this.cellSize / 2
        )
        .attr('font-weight', 'bold')
        .attr('text-anchor', 'end')
        .attr('alignment-baseline', 'middle')
        .text(
          ([key]) => `
            ${key.split('-')[0]}
            ${key.split('-')[1]}
          `
        )

      year
        .append('g')
        .selectAll('rect')
        .data(([, values]) => values)
        .join('rect')
        .attr('width', this.cellSize - 1)
        .attr('height', this.cellSize - 1)
        // .attr("x", ([d]) => timeWeek.count(d3.utcYear(getUtcDate(d)), getUtcDate(d)) * this.cellSize + 0.5)
        .attr('y', ([d]) => {
          return (
            timeWeek.count(getUtcDate(d), getUtcDate(this.endDate)) *
            this.cellSize
          ) // + 0.5
        })
        .attr('x', ([d]) => countDay(getUtcDate(d)) * this.cellSize)
        .attr('fill', ([, values]: CalendarDateGroupItem): string => {
          const total = values.reduce(
            (acc: number, v: CalendarDataItem) => acc + v.value,
            0
          )
          return total <= 0
            ? ZERO_COLOR
            : this.color(total)?.toString() ?? ZERO_COLOR
        })
        // .attr('stroke', ([key]) => key === TODAY ? 'blue' : 'grey')
        // .attr('stroke-width', 1)
        .append('title')
        .text(
          ([d, values]: CalendarDateGroupItem) => `
            ${formatDate(getUtcDate(d))}
            ${[
              ...new Set(
                values
                  .map((v: CalendarDataItem) => v.type)
                  .filter((v) => v !== 'none')
              ),
            ].join(', ')}: ${formatValue(
            values.reduce(
              (acc: number, v: CalendarDataItem) => acc + v.value,
              0
            )
          )}
          `
        )
    },
    draw() {
      if (!this.$refs.svg) throw new Error('no svg')

      // eslint-ignore-next-line
      this.svg = d3
        .select<Element, CalendarYearMonthGroupItem[]>(
          this.$refs.svg as Element
        )
        .attr('viewBox', `0, 0, ${this.width}, ${this.height}`)
        .attr('font-family', 'sans-serif')
        .attr('font-size', 10)

      this.svg
        .append('g')
        .attr('class', 'day-headers')
        .attr('transform', () => `translate(${50}, ${0})`)
        .attr('text-anchor', 'middle')
        .attr('alignment-baseline', 'middle')
        .selectAll('text')
        .data(d3.range(7).map((i) => new Date(1995, 0, i)))
        .join('text')
        // .attr("x", -5)
        .attr('x', (d) => countDay(d) * this.cellSize + this.cellSize / 2)
        // .attr("y", d => (countDay(d) + 0.5) * this.cellSize)
        .attr('y', this.cellSize / 2)
        // .attr("dy", "0.31em")
        .text(formatDay)

      this.svg.append('g').attr('class', 'main-group')

      this.drawData()

      return this.svg.node()
    },
  },
})
